import { useEffect } from 'react';
import { toast } from 'react-toastify';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  APIError,
  ClientError,
  useErrorHandler,
  useAuth,
  useLocale,
  useMutation,
  useModalState,
  useQuery,
} from 'shared-ui';

import { changeUserPasswordWithConfirmation } from 'api/users/users';
import { AppMessages } from 'i18n/messages';
import { User } from 'api/users/users.types';
import { QueryKeyEnum } from 'core/global.enum';
import { fetchPasswordPolicy } from 'api/auth/auth';

import { ChangePasswordForm } from './ChangePassword.types';
import { ChangePassword } from './ChangePassword';
import { useChangePasswordValidation } from './ChangePassword.validation';

export const ChangePasswordContainer = () => {
  const { formatMessage } = useLocale();
  const { user } = useAuth<User, unknown, unknown>();
  const { isModalOpen, onModalOpen, onModalClose } = useModalState();
  const { handleError } = useErrorHandler();

  const { data: passwordPolicy } = useQuery([QueryKeyEnum.PASSWORD_POLICY], fetchPasswordPolicy, {});

  const { mutate: onChangeUserPassword, isLoading } = useMutation(changeUserPasswordWithConfirmation, {
    onSuccess: () => {
      toast.dark(formatMessage({ id: AppMessages['profile.changePassword.success'] }));

      onModalClose();
    },
    onError: (error: ClientError<APIError>) => {
      const errorFields = error.response?.data.fields;

      const isSamePasswordError = errorFields?.find(
        ({ type, field }) => type === 'same_password' && field === 'newPassword',
      );
      if (isSamePasswordError) {
        return formMethods.setError('newPassword', {
          message: formatMessage({ id: AppMessages['profile.changePassword.validation.newDifference'] }),
        });
      }

      const isWrongOldPasswordError = errorFields?.find(
        ({ type, field }) => type === 'wrong_password' && field === 'oldPassword',
      );
      if (isWrongOldPasswordError) {
        return formMethods.setError('oldPassword', {
          message: formatMessage({ id: AppMessages['profile.changePassword.validation.oldPassword.wrong'] }),
        });
      }

      handleError({ error, omitAPIErrorMessage: true });
    },
  });

  const { schema } = useChangePasswordValidation(user, passwordPolicy);
  const formMethods = useForm<ChangePasswordForm>({
    resolver: yupResolver(schema),
    mode: 'all',
  });

  const onSubmit = (body: ChangePasswordForm) => {
    onChangeUserPassword({ username: user?.username as string, body });
  };

  useEffect(() => {
    formMethods.reset();
  }, [isModalOpen]);

  return (
    <FormProvider {...formMethods}>
      <ChangePassword
        isModalOpen={isModalOpen}
        onModalClose={onModalClose}
        onModalOpen={onModalOpen}
        isLoading={isLoading}
        onSubmit={onSubmit}
        passwordPolicy={passwordPolicy}
      />
    </FormProvider>
  );
};
